import React from 'react'
import { Button } from 'src/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from 'src/components/ui/collapsible';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'src/components/ui/table';
import * as Icon from 'lucide-react'
import { convertToDate } from 'src/lib/date';
import { format, parse, getISOWeek } from 'date-fns';
import { DoctypeLabel } from '../../../helpers/doctype-label'
import { handleDownloadDocument } from 'src/components/utils/utils';
import { useSearchParams } from 'react-router-dom';
import { ContentLabel } from '../../../helpers/content-label'

type Props = {
    results: any[];
    loadMore: () => void;
    isPending: boolean;
    hasMore: boolean;

    display?: "default" | "by-week"
}

function groupByWeek(input: any[]): any[] {
    const weekMap = new Map<number, any[]>();
  
    input.forEach((item) => {
      const parsedDate = parse(item.docdate, 'yyyyMMdd', new Date());
      const weekNumber = getISOWeek(parsedDate);
  
      if (!weekMap.has(weekNumber)) {
        weekMap.set(weekNumber, []);
      }
      weekMap.get(weekNumber)?.push(item);
    });
  
    return Array.from(weekMap.entries()).map(([weekNumber, data]) => ({
      weekNumber,
      data,
    }));
}

const Results = ({results, loadMore, isPending, hasMore, display="default"}:Props) => {
    const [searchParams, setSearchParams] = useSearchParams();

    function handleOpenDocument (docid:string | number) {
        searchParams.set('docid', docid.toString())
        setSearchParams(searchParams)
    }

    if(!results || results?.length === 0) { return null }

    if(display === "default") {
        return (
            <div>
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>
                                <ContentLabel path="dashboard.lastopened_table_files" />
                            </TableHead>
                            <TableHead>
                                <ContentLabel path="dashboard.lastopened_table_type" />
                            </TableHead>
                            <TableHead>
                                <ContentLabel path="dashboard.lastopened_table_date" />
                            </TableHead>
                            <TableHead></TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {results?.map((d) => {
                            const docdate = convertToDate(d?.docdate)
                            return (
                                <TableRow onClick={()=>{handleOpenDocument(d?.docid)}} key={`${d?.docid}`} className='cursor-pointer group/row'>
                                    <TableCell>
                                        <p className='font-medium group-hover/row:text-primary group-hover/row:underline'>{d?.filename}</p>
                                    </TableCell>
                                    <TableCell>
                                        <DoctypeLabel doctype={d?.doctype}/>
                                    </TableCell>
                                    <TableCell>
                                        <p>{docdate ? format(docdate, 'd MMM yyyy') : ''}</p>
                                    </TableCell>
                                    <TableCell>
                                        <div className='flex items-center justify-end'>
                                            <button 
                                                onClick={()=>{
                                                    handleDownloadDocument(d?.docid);
                                                }}
                                                className='text-neutral-500 hover:text-primary'
                                            >
                                                <Icon.Download className='w-4 h-4'/>
                                            </button>
                                        </div>
                                    </TableCell>
                                </TableRow>
                            )
                        })}
                    </TableBody>
                </Table>

                {hasMore &&
                <div className='flex items-center justify-center mt-4'>
                    <Button
                        onClick={()=>{loadMore()}}
                        disabled={isPending}
                    >
                        {isPending && <Icon.Loader2 className='w-4 h-4 animate-spin'/>}
                        Meer inladen
                    </Button>
                </div>
                }
            </div>
        )
    }

    else if(display === "by-week") {
        return (
            <div className='flex flex-col gap-2'>
                {groupByWeek(results).map((week, index) => {
                    return (
                        <Collapsible key={`week-${index}`} className='w-full border'>
                            <CollapsibleTrigger className='w-full font-semibold'>
                                <div className='flex items-center justify-between w-full gap-4 p-4'>
                                    <p>Week {week?.weekNumber || 0} - {week?.data?.length || 0} documenten</p>
                                    <Icon.ChevronDown className='w-4 h-4'/>
                                </div>
                            </CollapsibleTrigger>
                            <CollapsibleContent className='border-t'>
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>
                                                <ContentLabel path="dashboard.lastopened_table_files" />
                                            </TableHead>
                                            <TableHead>
                                                <ContentLabel path="dashboard.lastopened_table_type" />
                                            </TableHead>
                                            <TableHead>
                                                <ContentLabel path="dashboard.lastopened_table_date" />
                                            </TableHead>
                                            <TableHead></TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                    {week?.data?.map((d:any) => {
                                        const docdate = convertToDate(d?.docdate)
                                        return (
                                            <TableRow onClick={()=>{handleOpenDocument(d?.docid)}} key={`${d?.docid}`} className='cursor-pointer group/row'>
                                                <TableCell>
                                                    <p className='font-medium group-hover/row:text-primary group-hover/row:underline'>{d?.filename}</p>
                                                </TableCell>
                                                <TableCell>
                                                    <DoctypeLabel doctype={d?.doctype}/>
                                                </TableCell>
                                                <TableCell>
                                                    <p>{docdate ? format(docdate, 'd MMM yyyy') : ''}</p>
                                                </TableCell>
                                                <TableCell>
                                                    <div className='flex items-center justify-end'>
                                                        <button 
                                                            onClick={()=>{
                                                                handleDownloadDocument(d?.docid);
                                                            }}
                                                            className='text-neutral-500 hover:text-primary'
                                                        >
                                                            <Icon.Download className='w-4 h-4'/>
                                                        </button>
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                        )
                                    })}
                                    </TableBody>
                                </Table>
                            </CollapsibleContent>
                        </Collapsible>
                    )
                })}

                {hasMore &&
                <div className='flex items-center justify-center mt-4'>
                    <Button
                        onClick={()=>{loadMore()}}
                    >
                        {isPending && <Icon.Loader2 className='w-4 h-4 animate-spin'/>}
                        Meer inladen
                    </Button>
                </div>
                }
            </div>
        )
    }

    else return null;
}

export default Results